import { isPast } from "date-fns";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { useGetAuthUser } from "@/api/get-user";
import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import { EventThumbnail } from "@/features/events/components/event-thumbnail";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { PublicEvent } from "@classview/shared-types";

import { useAddParticipantToEvent } from "../api/add-participant-to-event";
import { isUserAttendingEvent } from "../api/is-user-participating-in-event";

import { EventDetails } from "./event-details";
import { EventOrganiser } from "./event-organiser";

type EventItemProps = {
  event: PublicEvent;
};

export function EventItem({ event }: EventItemProps) {
  const { organisation } = useOrganisation();
  const { data: user } = useGetAuthUser();
  const { mutateAsync: addAttendeeToEvent, isPending } =
    useAddParticipantToEvent({
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes("events"),
        });
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  const isAttending = isUserAttendingEvent(event, user?.sub ?? "");

  const handleJoinEvent = () => {
    addAttendeeToEvent({
      eventId: event.id,
      payload: {
        participants: [{ userId: user?.sub }],
      },
      orgId: organisation.id,
    });
  };

  const isLessThen5SpotsLeft =
    event.isCapacityCapped &&
    event.capacityLimit - (event.session.attendees?.length ?? 0) < 5;
  const isEventFull =
    event.isCapacityCapped &&
    event.capacityLimit === event.session.attendees?.length;
  const isCurrentUserOrganiser = event.session.organiser.id === user?.sub;
  const isEventPast = isPast(event.session.start);
  const isRegisterButtonShown =
    !isAttending && !isCurrentUserOrganiser && !isEventPast && !isEventFull;
  return (
    <Card className="flex flex-1" as="article">
      <Link className="group relative block" to={`/events/${event.id}`}>
        <EventThumbnail
          isAttending={isAttending}
          category={event.category}
          bannerImageUrl={event.bannerS3Path}
          className="h-[200px]"
          isLessThen5SpotsLeft={isLessThen5SpotsLeft}
          isEventFull={isEventFull}
        />
      </Link>
      <Card.Content className="flex h-full flex-1 flex-col pt-3">
        <div className="text-md font-semibold text-foreground">
          {event.session.name}
        </div>
        <div className="mb-5 mt-2 flex flex-row items-center justify-between">
          <EventDetails
            type={event.type}
            startAt={event.session.start}
            endAt={event.session.end}
          />
        </div>
        <div className="mt-auto flex w-full flex-col gap-6">
          <EventOrganiser
            firstName={event.session.organiser.firstName}
            lastName={event.session.organiser.lastName}
            avatarURL={event.session.organiser.avatarURL}
          />
          <div className="mt-auto flex w-full flex-1 items-center gap-2">
            {isRegisterButtonShown && (
              <Button
                variant="outline"
                className="w-[50%]"
                onClick={handleJoinEvent}
                isLoading={isPending}
                isAnimated={isPending}
                disabled={isPending}
              >
                Quick Register
              </Button>
            )}

            <Button variant="default" asChild>
              <Link
                to={`/events/${event.id}`}
                className={cn(!isRegisterButtonShown ? "w-full" : "w-[50%]")}
              >
                View Details
              </Link>
            </Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}
